import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Button,
  Typography,
  Grid,
  Stack,
  Pagination,
  useTheme,
  useMediaQuery,
  Divider
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DataGrid } from "@mui/x-data-grid";
import format from "date-fns/format";
import { lighten } from "@material-ui/core";
import { FocusTrap } from '@mui/base/FocusTrap';
import { useGetPromotionRecordMutation } from "../../features/api/bonusApiSlice";


const OngoingBonus = (props) => {
  const location = useLocation();

  const theme = useTheme();
  const { t } = useTranslation();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = new Date();
  const sevenDaysAgo = new Date();
  const isLightTheme = process.env.REACT_APP_IS_LIGHT_THEME === "true";

  const withdrawDetails =
    props.withdrawDetails || location.state?.withdrawDetails;

  const type = [
    { label: t("walletcard.transactionpage.all"), value: "All" },
    { label: t("walletcard.transactionpage.approved"), value: "Approved" },
    { label: t("walletcard.transactionpage.rejected"), value: "Rejected" },
    { label: t("walletcard.transactionpage.pending"), value: "Pending" },
  ];

  const [statusType, setStatusType] = useState(type[0].value);
  const [startDate, setStartDate] = useState(
    sevenDaysAgo.setDate(currentDate.getDate() - 7)
  );
  const [endDate, setEndDate] = useState(currentDate);
  const [offset, setOffset] = useState(0);

  const [
    promotionRecord,
    result,
  ] = useGetPromotionRecordMutation();

  useEffect(() => {
    if (!(endDate < startDate)) {
      let data = {
        criteria: {
          dateFrom: format(startDate, "dd-MM-yyyy"),
          dateTo: format(endDate, "dd-MM-yyyy"),
          status: statusType.toUpperCase(),
        },
        offset: offset,
      };
      promotionRecord(data).unwrap();
    }
  }, [startDate, endDate, statusType, offset]);


  const [openStates, setOpenStates] = useState(Array(result?.data?.result?.content.length).fill(false));

  const toggleOpenState = (index) => {
    setOpenStates(prevStates => {
      const newStates = [...prevStates];
      newStates[index] = !newStates[index];
      return newStates;
    });
  };

  const withdrawalType = [];
  withdrawalType.push({ label: t("walletcard.depositpage.title") });

  const handlePageChange = (e, p) => {
    setOffset(p - 1);
  };

  return (
    <Container sx={{
      background: !isLightTheme && mobileView ? theme.palette.background.paper : undefined,
      py: {
        xs: 10, sm: 0, md: 0, lg: 0
      },
      px: mobileView ? 3.5 : undefined,
      minHeight: mobileView ? "100vh" : undefined
    }}>
      {
        !mobileView && (
          <Typography fontWeight="bold" color={process.env.REACT_APP_COLOR_TEXT || "white"}>
            {t("walletcard.ongoingbonus")}
          </Typography>
        )
      }

      <Box
        border={1}
        borderRadius={1}
        padding={1}
        mt={!mobileView ? 2 : undefined}
        borderColor='#838383'
        width={mobileView ? 'auto' : '40%'}
      >
        <Grid container spacing={1} alignItems="center" wrap="nowrap">
          <Grid item xs="auto">
            <Typography noWrap fontSize={mobileView ? "small" : "14px"} color={process.env.REACT_APP_COLOR_TEXT || "white"}>
              {t("walletcard.withdrawalpage.weeklyTurnOver")} :
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography noWrap fontWeight="bold" fontSize="14px" color={process.env.REACT_APP_COLOR_TEXT || "white"}>
              {process.env.REACT_APP_CURRENCY_CODE} {withdrawDetails?.playerTurnOver?.weeklyTurnOver || "0.00"}
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={1} alignItems="center" wrap="nowrap">
          <Grid item xs="auto">
            <Typography noWrap fontSize={mobileView ? "small" : "14px"} color={process.env.REACT_APP_COLOR_TEXT || "white"}>
              {t("walletcard.withdrawalpage.monthlyTurnOver")} :
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Typography noWrap fontWeight="bold" fontSize="14px" color={process.env.REACT_APP_COLOR_TEXT || "white"}>
              {process.env.REACT_APP_CURRENCY_CODE} {withdrawDetails?.playerTurnOver?.monthlyTurnOver || "0.00"}
            </Typography>
          </Grid>
        </Grid>
      </Box>


      {!mobileView && (
        <>
          <Box sx={{ height: 400, width: "100%", mt: "30px" }}>
            <DataGrid
              sx={{
                '& .MuiDataGrid-cell': {
                  justifyContent: 'center',
                  textAlign: 'center',
                },
                '.MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '&.MuiDataGrid-root': {
                  border: 'none',
                },
                "& .MuiDialogContent-root": {
                  overflowY: "auto",
                  "::-webkit-scrollbar": {
                    width: "12px",
                  },
                  "::-webkit-scrollbar-track": {
                    background: theme.palette.text.disabled,
                  },
                  "::-webkit-scrollbar-thumb": {
                    background: theme.palette.background.paper,
                  },
                },
                color: "white",
                backgroundColor: theme.palette.secondary.accent,
              }}
              rows={
                result?.data?.result?.content?.slice(0, 5).sort((a, b) => {
                  // Priority 1: Sort by status "Ongoing" first
                  if (a.status === "ONGOING" && b.status !== "ONGOING") return -1;
                  if (a.status !== "ONGOING" && b.status === "ONGOING") return 1;

                  // Priority 2: Sort by createdDate (newest first)
                  return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                }).map((content, idx) => {
                  return {
                    ...content,
                    createdDate: format(
                      new Date(content?.createdDate),
                      "dd-MM-yyyy"
                    ),
                    id: idx + 1,
                  };
                }) || []
              }
              columns={[
                {
                  field: "createdDate",
                  headerName: t("walletcard.walletpage.period"),
                  width: 150,
                  editable: false,
                  headerAlign: 'center',
                },
                {
                  field: "title",
                  headerName: t("walletcard.withdrawalpage.title"),
                  width: 150,
                  editable: false,
                  headerAlign: 'center',
                },
                {
                  field: "validTurnOver",
                  headerName: t("walletcard.withdrawalpage.validbet"),
                  width: 150,
                  editable: false,
                  headerAlign: 'center',
                },
                {
                  field: "turnover",
                  headerName: t("walletcard.withdrawalpage.validbetcondition"),
                  width: 150,
                  editable: false,
                  headerAlign: 'center',
                },
                {
                  field: "status",
                  headerName: t("walletcard.walletpage.status"),
                  width: 150,
                  editable: false,
                  headerAlign: 'center',
                }
              ]}
              hideFooter={true}
              rowsPerPageOptions={[5]}
              onPageChange={(newPage) => setOffset(newPage)}
            />
          </Box>
          <Stack mt={2} spacing={2} size="small" alignItems="center">
            <Pagination
              onChange={handlePageChange}
              count={
                result?.data?.result?.totalPages === 0
                  ? 1
                  : result?.data?.result?.totalPages
              }
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-root": {
                  color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER2 || "white",
                  "&.Mui-selected": {
                    background: lighten(
                      process.env.REACT_APP_COLOR_PAGINATION || theme.palette.background.paper,
                      0.02
                    ),
                    color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER1 || "white",
                  },
                },
                "& .MuiPaginationItem-icon": {
                  color: process.env.REACT_APP_COLOR_PAGINATION_NUMBER2 || "white",
                },
              }}
            />
          </Stack>
        </>
      )}
      <>
        {
          mobileView && result?.data?.result?.content.length === 0 && (
            <Typography
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || "white",
                mt: 2,
                textAlign: "center",
              }}
            >
              {t("promocenter.norecord")}
            </Typography>
          )
        }

        {mobileView && result?.data?.result?.content.length > 0 && result?.data?.result?.content?.slice(0, 5).sort((a, b) => {
          if (a.status === "ONGOING" && b.status !== "ONGOING") return -1;
          if (a.status !== "ONGOING" && b.status === "ONGOING") return 1;

          if (result?.data?.result?.content.length === 0) {
            return (
              <Typography sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white", mt: 2, textAlign: "center" }}>
                {t("promocenter.norecord")} </Typography>
            )
          }

          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        }).map((content, idx) => (
          <Box key={idx} sx={{ border: `1px solid ${process.env.REACT_APP_COLOR_TEXT || 'white'}` }} marginTop="10px">
            <Button
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
              onClick={() => toggleOpenState(idx)}
            >
              <div>
                <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', textAlign: 'left' }}>
                  {new Date(content.createdDate).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                  })}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: process.env.REACT_APP_COLOR_TEXT || 'white', fontWeight: 'bold' }}>
                  {content.title}
                </Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', width: '80px', height: '30px', backgroundColor: '#838383', lineHeight: '40px', padding: '5px', borderRadius: '5px' }}>
                <Typography sx={{ color: 'white', textAlign: 'center', fontSize: '10px' }}>
                  {content.status}
                </Typography>
              </div>
            </Button>
            <FocusTrap key={idx} open={openStates[idx]} disableRestoreFocus disableAutoFocus>
              <Stack alignItems="center" spacing={2}>
                {openStates[idx] ? (
                  <Divider sx={{ backgroundColor: 'white', width: '100%' }} />
                ) : (
                  <Button onClick={() => toggleOpenState(idx)}>
                    <Typography sx={{ fontSize: '11px', color: process.env.REACT_APP_COLOR_TEXT || 'white', fontWeight: 'bold' }}>
                      More
                    </Typography>
                  </Button>
                )}
                {openStates[idx] && (
                  <Grid container >
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', paddingLeft: '8px' }}>{t("walletcard.withdrawalpage.deposit")}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', paddingRight: '8px' }} align="right">{process.env.REACT_APP_CURRENCY_CODE} {content.deposit}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', paddingLeft: '8px' }}>{t("walletcard.withdrawalpage.title")}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', paddingRight: '8px' }} align="right">{content.title}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', paddingLeft: '8px' }}>{t("walletcard.withdrawalpage.validbet")}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', paddingRight: '8px' }} align="right">{process.env.REACT_APP_CURRENCY_CODE} {content.validTurnOver}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', paddingLeft: '8px', paddingBottom: '6px' }}>{t("walletcard.withdrawalpage.validbetcondition")}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '10px', color: process.env.REACT_APP_COLOR_TEXT || 'white', paddingRight: '8px', paddingBottom: '6px' }} align="right">{process.env.REACT_APP_CURRENCY_CODE} {content.turnover}</Typography>
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </FocusTrap>
          </Box>
        ))
        }
      </>
    </Container>
  );
};

export default OngoingBonus;
